import { useCallback, useMemo, useState, type ReactNode } from "react";
import { can, scopeFor, type Role } from "@sts/schemas";
import type { Deadline, Job } from "../api/types.js";
import { ApiError, refusedAction } from "../api/client.js";
import { Async, Notices } from "../components/Async.jsx";
import { UrgencyBadge } from "../components/Chips.jsx";
import { IconState } from "../components/Icons.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { href } from "../app/router.js";
import { PIPELINE_ORDER, STAGE_LABEL, isClosed } from "../lib/stages.js";
import { worstUrgency } from "../lib/deadlines.js";
import { isoDay, longDate, utcMoment } from "../lib/format.js";

/**
 * The pipeline board.
 *
 * Built from `GET /api/jobs` — this organisation's own jobs — or, for a role
 * scoped to its own bookings, `GET /api/jobs?mine=true`. Which one is asked is
 * decided up front from the same permission matrix the API enforces, the way
 * `Tasks` decides between the whole organisation's inbox and `?mine=true`, so
 * this never has to discover a 403 to find out which list it is allowed.
 *
 * This used to be assembled by hand from job ids that turned up on the
 * deadline board or a task, plus whatever a browser happened to remember from
 * creating or opening a job — the API had `POST /api/jobs` and
 * `GET /api/jobs/:id` and nothing in between. That gap is what closed: a job
 * with no task, no deadline and no history in this browser now still appears.
 */

interface PipelineData {
  readonly jobs: readonly Job[];
  /** The signed-in role, for the controls the board may or may not offer. */
  readonly role: Role;
  readonly deadlines: readonly Deadline[];
  readonly scope: "organisation" | "mine";
  readonly notes: readonly string[];
}

export function Pipeline(): ReactNode {
  const { client } = usePortal();

  const { state, reload } = useAsync<PipelineData>(async () => {
    const notes: string[] = [];
    const me = await client.me();
    const scope: "organisation" | "mine" =
      scopeFor(me.role, "job", "read") === "org" ? "organisation" : "mine";

    const [jobResult, deadlineResult] = await Promise.allSettled([
      client.jobs(scope === "mine" ? { mine: true } : {}),
      client.deadlines(),
    ]);

    const jobs = unwrap(jobResult, notes, "the pipeline");
    const deadlines = unwrap(deadlineResult, notes, "the deadline board");

    return { jobs, deadlines, scope, notes, role: me.role };
  }, [client]);

  return (
    <>
      <div className="page-header">
        <h1>Pipeline</h1>
        <button className="secondary" onClick={reload}>
          Refresh
        </button>
        <p className="lede">
          Every job this organisation holds, grouped by the stage it has
          reached.
        </p>
      </div>

      <Async state={state} what="the pipeline" reload={reload}>
        {(data) => <Board data={data} />}
      </Async>
    </>
  );
}

/** Settles one list, turning a 403 into a note rather than failing the board. */
function unwrap<T>(
  result: PromiseSettledResult<T[]>,
  notes: string[],
  what: string,
): T[] {
  if (result.status === "fulfilled") return result.value;

  const error: unknown = result.reason;
  if (error instanceof ApiError && error.status === 403) {
    const refused = refusedAction(error);
    notes.push(
      refused
        ? `${what} was refused: you may not ${refused.action} ${refused.resource} across the organisation.`
        : `${what} is not visible to your role.`,
    );
    return [];
  }
  notes.push(`${what} could not be loaded.`);
  return [];
}

/**
 * What a job is searched BY.
 *
 * The reference first, because on this platform it is usually the address:
 * a survey arriving from a phone keeps the address the surveyor typed as the
 * job's name, so "Cheddar", "M18" and "Gorton" all find
 * "22 Cheddar st Gorton, M18 8QB" — which is how somebody actually looks for
 * a job. The stage is included so "survey" or "design" narrows the board to
 * one column, and the syncing surveyor's name so "who did Sam send?" is a
 * question the box can answer.
 *
 * Case and the gaps between words are ignored, so "ch62 1eu" finds
 * "CH62  1EU"; the needle is matched as ONE string against the haystack
 * rather than word by word, which keeps "26 laurel" from matching a job that
 * merely has a 26 and a laurel somewhere apart.
 */
/**
 * The two sections the board can be read in, and the one nobody asked for.
 *
 * SYNCED and COMPLETE are the owner's: a survey has arrived from a phone, or
 * somebody has said the visit is finished. The third, NONE, exists because a
 * job with no survey has to go SOMEWHERE — dropping it would take a job off
 * the board entirely, and a board that silently omits work is worse than one
 * with an extra heading.
 *
 * THE ORDER IS BY HOW MUCH IS LEFT TO DO, most first. Synced leads: a survey
 * has landed and nobody has done anything with it yet, which is the only
 * section that is actually a queue. No survey yet follows — a job waiting for
 * a visit, which is work, just not work on this screen. Survey complete is
 * LAST, because it is the one section nobody needs to act on, and it used to
 * be first: a single finished job sat above ninety-nine that still needed
 * reading, pushing the queue below the fold.
 *
 * "superseded" sits with the synced ones: a survey replaced by a newer one is
 * still a survey that arrived, and the job's newest survey is what the row is
 * about. A job cannot be in two sections.
 */
const SURVEY_SECTIONS = ["synced", "none", "complete"] as const;
type SurveySection = (typeof SURVEY_SECTIONS)[number];

const SURVEY_SECTION_LABEL: Record<SurveySection, string> = {
  complete: "Survey complete",
  synced: "Synced",
  none: "No survey yet",
};

function sectionOf(job: Job): SurveySection {
  // undefined (an API older than the field) is treated as null: the board
  // falls back to "has it ever synced?", which is what it could answer before.
  const status = job.last_survey_status ?? null;
  if (status === "complete") return "complete";
  if (status !== null) return "synced";
  return job.last_survey_synced_at ? "synced" : "none";
}

/**
 * The order the rows are read in.
 *
 * NEWEST FIRST IS THE DEFAULT. It used to be "reference", on the reasoning
 * that the board should not rearrange itself under somebody who did not ask —
 * which was the wrong question. This board is a work queue: surveys arrive
 * from the field all day and the one that arrived twenty minutes ago is the
 * one somebody is looking for. Alphabetical order by reference put it
 * wherever the alphabet happened to put it, so the first thing anybody did on
 * opening the board was change the order, every time.
 *
 * The two date orders are on the date the newest survey ARRIVED — the same
 * date the range filter uses, so sorting and filtering cannot disagree about
 * which date a row has.
 */
const SORTS = [
  { key: "newest", label: "Survey arrived — newest first" },
  { key: "oldest", label: "Survey arrived — oldest first" },
  { key: "reference", label: "Reference (A–Z)" },
] as const;
type SortKey = (typeof SORTS)[number]["key"];

/** How far back the board looks, by the date the newest survey arrived. */
const DATE_RANGES = [
  { key: "all", label: "Any time", days: null },
  { key: "7", label: "Last 7 days", days: 7 },
  { key: "30", label: "Last 30 days", days: 30 },
  { key: "90", label: "Last 90 days", days: 90 },
] as const;
type DateRangeKey = (typeof DATE_RANGES)[number]["key"];

/**
 * Has a survey arrived on this job that this reader has not opened?
 *
 * Two moments, compared: when the newest survey arrived, and when this person
 * last opened the job (migration 0068, per reader). That comparison — rather
 * than a "seen" flag — is what makes a RE-SURVEYED job go unread again. A job
 * opened last week and synced again this morning has been opened, and still
 * has something nobody has read.
 *
 * A job with no survey is never marked. Nothing has arrived, so there is
 * nothing to have missed, and marking every empty job would make the mark
 * worthless within a day.
 *
 * WHEN IT CANNOT TELL, IT SAYS NOTHING. An unparseable arrival date returns
 * false rather than true: a mark that appears because a timestamp was
 * malformed teaches people to ignore marks, and this one is only worth having
 * if it is believed.
 */
export function isUnopened(job: Job): boolean {
  const arrived = job.last_survey_synced_at ?? null;
  if (arrived === null) return false;
  const arrivedMs = Date.parse(arrived);
  if (Number.isNaN(arrivedMs)) return false;

  const seen = job.last_viewed_at ?? null;
  if (seen === null) return true;
  const seenMs = Date.parse(seen);
  // A survey arrived and the view date is unreadable: the arrival is the fact
  // we trust, so the row stays marked.
  if (Number.isNaN(seenMs)) return true;

  return arrivedMs > seenMs;
}

/**
 * The unread mark.
 *
 * A WORD, not a colour. These boards get printed for site meetings in black
 * and white, and some of the people reading them every day cannot tell a tinted
 * row from an untinted one — the same reason the evidence screens spell out
 * their states instead of shading them. The tint on the row is a second cue
 * for people who can see it, never the only one.
 */
function Unopened(): ReactNode {
  return (
    <span className="unread-mark" title="A survey has arrived that you have not opened">
      <span aria-hidden="true">●</span> New
    </span>
  );
}

function searchText(job: Job): string {
  return [job.reference, STAGE_LABEL[job.stage], job.last_survey_synced_by ?? ""]
    .join(" ")
    .toLowerCase()
    .replace(/\s+/g, " ");
}

const normalise = (needle: string): string => needle.trim().toLowerCase().replace(/\s+/g, " ");

function Board({ data }: { readonly data: PipelineData }): ReactNode {
  const { jobs, deadlines, scope, notes, role } = data;
  /**
   * The search box. Client-side on purpose: the board already holds every job
   * this role may see, so filtering here is instant, costs no request, and
   * works on a van's bad signal. It would have to become a server query only
   * if the board ever stopped loading the whole list.
   */
  const [query, setQuery] = useState("");
  /** Board is the kanban; list is one flat table with no grouping. */
  const [view, setView] = useState<"board" | "list">("board");
  /** Which two (three) sections the board is cut into, or the stage columns. */
  const [grouping, setGrouping] = useState<"survey" | "stage">("survey");
  const [range, setRange] = useState<DateRangeKey>("all");
  const [sort, setSort] = useState<SortKey>("newest");
  /**
   * Deleting a survey from the board.
   *
   * `survey:delete` is the owner's and the admin's — no other role sees the
   * control at all, rather than seeing one that always answers 403. The API
   * decides regardless; this only keeps a button off screens that cannot use
   * it.
   *
   * The delete is PERMANENT and takes the rooms, surfaces and captures with
   * it, so it is two deliberate presses (Delete, then Confirm) exactly as on
   * the survey's own page — and `removed` hides the row at once rather than
   * waiting for a refetch, because a row that lingers after a confirmed
   * delete invites a second press on something that is already gone.
   */
  const { client } = usePortal();
  const canDelete = can({ role, resource: "survey", action: "delete" });
  const [deleting, setDeleting] = useState<string | null>(null);
  const [removed, setRemoved] = useState<readonly string[]>([]);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const onDeleted = useCallback(
    async (surveyId: string) => {
      setDeleting(surveyId);
      setDeleteError(null);
      try {
        await client.deleteSurvey(surveyId);
        setRemoved((was) => [...was, surveyId]);
      } catch (err) {
        const refusal = err instanceof ApiError ? refusedAction(err) : null;
        setDeleteError(
          refusal
            ? `Your role cannot ${refusal.action} a ${refusal.resource}. Ask an owner or an administrator.`
            : err instanceof ApiError
              ? err.message
              : "That survey could not be deleted just now.",
        );
      } finally {
        setDeleting(null);
      }
    },
    [client],
  );

  const needle = normalise(query);
  const matching = useMemo(() => {
    const days = DATE_RANGES.find((r) => r.key === range)?.days ?? null;
    // Measured from now, against the date the newest survey arrived. A job
    // with no survey has no such date, so it cannot satisfy a range — it is
    // not "older than 90 days", it is undated, and showing it under "last 7
    // days" would be a lie about when anything happened.
    const cutoff = days === null ? null : Date.now() - days * 86_400_000;
    return jobs.filter((job) => {
      // A survey deleted in this session takes its job out of the survey
      // sections immediately — the row would otherwise sit there claiming a
      // survey that no longer exists until the next load.
      if (job.last_survey_id && removed.includes(job.last_survey_id)) return false;
      if (needle !== "" && !searchText(job).includes(needle)) return false;
      if (cutoff === null) return true;
      const at = job.last_survey_synced_at;
      return at !== null && at !== undefined && Date.parse(at) >= cutoff;
    });
  }, [jobs, needle, range, removed]);

  const shown = useMemo(() => {
    if (sort === "reference") return matching;
    // A job with NO survey has no date to sort by. It goes last in BOTH
    // directions rather than being treated as infinitely old or infinitely
    // new: "undated" is a third thing, and letting it win either end of the
    // list would be a claim about when something happened.
    const when = (job: Job): number | null => {
      const at = job.last_survey_synced_at;
      return at === null || at === undefined ? null : Date.parse(at);
    };
    return [...matching].sort((a, b) => {
      const left = when(a);
      const right = when(b);
      if (left === null && right === null) return a.reference.localeCompare(b.reference);
      if (left === null) return 1;
      if (right === null) return -1;
      if (left === right) return a.reference.localeCompare(b.reference);
      return sort === "newest" ? right - left : left - right;
    });
  }, [matching, sort]);

  if (jobs.length === 0) {
    return (
      <>
        <Notices notes={notes} />
        <div className="state state--empty">
          <span className="state__glyph">
            <IconState kind="empty" />
          </span>
          <p className="state__title">Nothing on the board</p>
          <p className="state__body">
            {scope === "mine"
              ? "No jobs are booked to you at the moment."
              : "No jobs yet. A lead becomes the first row on this board."}
          </p>
          {scope === "mine" ? null : (
            <a className="cta" href={href.newLead()}>
              Take a lead
            </a>
          )}
        </div>
      </>
    );
  }

  /**
   * The columns, however the board is being cut. One shape for both groupings
   * so the kanban and the list below do not each need to know which is in
   * play — only the headings differ.
   *
   * An EMPTY section is kept when grouping by survey and dropped when
   * grouping by stage. A stage nobody is at is noise; "Survey complete" with
   * nothing in it is the whole point — it says the work is not being marked
   * finished, which on this data is exactly what is happening (58 draft, 1
   * complete on 2026-10-07).
   */
  const groups = useMemo(() => {
    if (grouping === "stage") {
      return PIPELINE_ORDER.map((stage) => ({
        key: stage as string,
        label: STAGE_LABEL[stage],
        closed: isClosed(stage),
        jobs: shown.filter((job) => job.stage === stage),
      })).filter((group) => group.jobs.length > 0);
    }
    return SURVEY_SECTIONS.map((section) => ({
      key: section as string,
      label: SURVEY_SECTION_LABEL[section],
      closed: section === "none",
      jobs: shown.filter((job) => sectionOf(job) === section),
    })).filter((group) => group.jobs.length > 0 || group.key !== "none");
  }, [shown, grouping]);

  const search = (
    <div className="board-search">
      <label className="board-search__label" htmlFor="pipeline-search">
        Search jobs
      </label>
      <input
        id="pipeline-search"
        type="search"
        className="board-search__input"
        placeholder="Reference, address, postcode, stage…"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        autoComplete="off"
        // Announced to a screen reader as the count below changes, so the
        // result of typing is heard and not only seen.
        aria-describedby="pipeline-search-count"
      />
      {query !== "" ? (
        <button
          type="button"
          className="board-search__clear"
          onClick={() => setQuery("")}
        >
          Clear
        </button>
      ) : null}
    </div>
  );

  return (
    <>
      <Notices notes={notes} />
      {scope === "mine" ? (
        <p className="muted small">
          Your role sees its own bookings rather than the whole organisation's
          pipeline.
        </p>
      ) : null}
      {search}

      {deleteError ? (
        <p className="notice notice--error" role="alert">
          {deleteError}
        </p>
      ) : null}

      <div className="board-controls" role="group" aria-label="How the board is shown">
        <Segmented
          legend="View"
          value={view}
          onChange={setView}
          options={[
            { value: "board", label: "Board" },
            { value: "list", label: "List" },
          ]}
        />
        <Segmented
          legend="Group by"
          value={grouping}
          onChange={setGrouping}
          options={[
            { value: "survey", label: "Survey" },
            { value: "stage", label: "Stage" },
          ]}
        />
        <label className="board-controls__date">
          <span>Order</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
            {SORTS.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="board-controls__date">
          <span>Survey arrived</span>
          <select value={range} onChange={(e) => setRange(e.target.value as DateRangeKey)}>
            {DATE_RANGES.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <p className="board-summary" id="pipeline-search-count" aria-live="polite">
        {needle === "" ? (
          <>
            {jobs.length} {jobs.length === 1 ? "job" : "jobs"} across{" "}
            {groups.length} {groups.length === 1 ? "section" : "sections"}
            {range === "all"
              ? ""
              : `, ${DATE_RANGES.find((r) => r.key === range)!.label.toLowerCase()}`}
            .
          </>
        ) : (
          <>
            {matching.length} of {jobs.length} {jobs.length === 1 ? "job" : "jobs"}{" "}
            {matching.length === 1 ? "matches" : "match"} “{query.trim()}”.
          </>
        )}
      </p>

      {/* A search that finds nothing must say so where the board would have
          been, not leave the page looking broken under a count of zero. */}
      {needle !== "" && matching.length === 0 ? (
        <div className="state state--empty">
          <span className="state__glyph">
            <IconState kind="empty" />
          </span>
          <p className="state__title">No job matches that</p>
          <p className="state__body">
            Nothing on this board matches “{query.trim()}”. Try part of the
            address or the postcode — a job is usually named after the property.
          </p>
          <button type="button" className="cta" onClick={() => setQuery("")}>
            Show every job
          </button>
        </div>
      ) : null}

      {view === "list" ? (
        <ListView
          jobs={shown}
          deadlines={deadlines}
          groups={groups}
          onDeleted={onDeleted}
          canDelete={canDelete}
          deleting={deleting}
          error={deleteError}
        />
      ) : (
        groups.map((group) => (
          <section
            key={group.key}
            className={`panel stage-group${group.closed ? " is-closed" : ""}`}
          >
            {/* The head is a strip rather than a heading floating over the
                table: a board is read by scanning down the left edge, and a
                filled bar gives the eye a place to stop at each section. */}
            <div className="stage-group__head">
              <span className="stage-group__dot" aria-hidden="true" />
              <h2>{group.label}</h2>
              <span className="count">{group.jobs.length}</span>
            </div>
            {group.jobs.length === 0 ? (
              <p className="muted small board-section__empty">
                Nothing here. A survey moves into this section when somebody
                marks the visit complete on the survey itself.
              </p>
            ) : (
              <div className="kanban">
                {group.jobs.map((job) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    worst={worstUrgency(deadlinesForJob(deadlines, job.id))}
                    soonest={soonestDays(deadlinesForJob(deadlines, job.id))}
                    canDelete={canDelete}
                    deleting={deleting === job.last_survey_id}
                    onDelete={onDeleted}
                  />
                ))}
              </div>
            )}
          </section>
        ))
      )}
    </>
  );
}

/**
 * A labelled row of mutually exclusive buttons. A radio group underneath,
 * because that is what it is: pressing one turns the others off, and a
 * screen reader should hear a choice rather than four unrelated buttons.
 */
function Segmented<T extends string>({
  legend,
  value,
  onChange,
  options,
}: {
  readonly legend: string;
  readonly value: T;
  readonly onChange: (next: T) => void;
  readonly options: readonly { readonly value: T; readonly label: string }[];
}): ReactNode {
  return (
    <fieldset className="segmented">
      <legend>{legend}</legend>
      {options.map((option) => (
        <label
          key={option.value}
          className={option.value === value ? "segmented__on" : undefined}
        >
          <input
            type="radio"
            name={`segmented-${legend.replace(/\s+/g, "-").toLowerCase()}`}
            checked={option.value === value}
            onChange={() => onChange(option.value)}
          />
          {option.label}
        </label>
      ))}
    </fieldset>
  );
}

/**
 * Delete this job's newest survey, in two presses.
 *
 * Shown only where there is a survey to delete and the role may delete one.
 * The second press is a different button in the same place — the pattern the
 * survey page already uses — so nothing is deleted by a double-click on the
 * first, and moving the mouse away is enough to think better of it.
 */
function DeleteSurvey({
  job,
  busy,
  onDelete,
}: {
  readonly job: Job;
  readonly busy: boolean;
  readonly onDelete: (surveyId: string) => void;
}): ReactNode {
  const [armed, setArmed] = useState(false);
  const surveyId = job.last_survey_id;
  if (!surveyId) return null;
  if (busy) return <span className="muted small">Deleting…</span>;
  if (!armed) {
    return (
      <button
        type="button"
        className="link-button danger"
        onClick={() => setArmed(true)}
        aria-label={`Delete the survey on ${job.reference}`}
      >
        Delete survey
      </button>
    );
  }
  return (
    <span className="confirm-delete">
      <button
        type="button"
        className="link-button danger"
        onClick={() => {
          setArmed(false);
          onDelete(surveyId);
        }}
        aria-label={`Confirm deleting the survey on ${job.reference}`}
      >
        Confirm delete
      </button>
      <button type="button" className="link-button" onClick={() => setArmed(false)}>
        Cancel
      </button>
    </span>
  );
}

/** One job as a card on the kanban. */
function JobCard({
  job,
  worst,
  soonest,
  canDelete,
  deleting,
  onDelete,
}: {
  readonly job: Job;
  readonly worst: string | null;
  readonly soonest: number | null;
  readonly canDelete: boolean;
  readonly deleting: boolean;
  readonly onDelete: (surveyId: string) => void;
}): ReactNode {
  return (
    <article
      className={`job-card${worst === "breached" ? " is-breached" : ""}${
        isUnopened(job) ? " is-unread" : ""
      }`}
    >
      <h3 className="job-card__ref">
        <a href={href.job(job.id)}>{job.reference}</a>
        {isUnopened(job) ? <Unopened /> : null}
      </h3>
      <p className="job-card__line">
        <SyncedBy job={job} />
      </p>
      <p className="job-card__line">
        {soonest === null ? (
          <span className="muted small">No clock running</span>
        ) : (
          <UrgencyBadge daysRemaining={soonest} />
        )}
      </p>
      {canDelete ? (
        <p className="job-card__actions">
          <DeleteSurvey job={job} busy={deleting} onDelete={onDelete} />
        </p>
      ) : null}
    </article>
  );
}

/**
 * Every matching job in one table, ungrouped, with the section it belongs to
 * as a column — the same information the kanban shows, for somebody who would
 * rather scan a list than read columns.
 */
function ListView({
  jobs,
  deadlines,
  groups,
  onDeleted,
  canDelete,
  deleting,
}: {
  readonly jobs: readonly Job[];
  readonly deadlines: readonly Deadline[];
  readonly groups: readonly { readonly key: string; readonly label: string }[];
  readonly onDeleted: (surveyId: string) => void;
  readonly canDelete: boolean;
  readonly deleting: string | null;
  readonly error: string | null;
}): ReactNode {
  const labelOf = (job: Job): string =>
    groups.find((g) => g.key === sectionOf(job))?.label ?? SURVEY_SECTION_LABEL[sectionOf(job)];

  return (
    <section className="panel">
      <div className="table-scroll">
        <table>
          <caption className="visually-hidden">Every job on this board</caption>
          <thead>
            <tr>
              <th scope="col">Reference</th>
              <th scope="col">Survey</th>
              <th scope="col">Survey synced</th>
              <th scope="col">Compliance</th>
              <th scope="col">Signed off</th>
              {canDelete ? <th scope="col">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr
                key={job.id}
                className={
                  [
                    worstUrgency(deadlinesForJob(deadlines, job.id)) === "breached"
                      ? "is-breached"
                      : null,
                    isUnopened(job) ? "is-unread" : null,
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
              >
                <th scope="row">
                  <a href={href.job(job.id)}>{job.reference}</a>
                  {isUnopened(job) ? <Unopened /> : null}
                </th>
                <td>{labelOf(job)}</td>
                <td>
                  <SyncedBy job={job} />
                </td>
                <td>
                  {soonestDays(deadlinesForJob(deadlines, job.id)) === null ? (
                    <span className="muted">No clock running</span>
                  ) : (
                    <UrgencyBadge
                      daysRemaining={soonestDays(deadlinesForJob(deadlines, job.id))!}
                    />
                  )}
                </td>
                <td>
                  {job.design_signed_off_at ? (
                    isoDay(job.design_signed_off_at)
                  ) : (
                    <span className="muted">Not signed off</span>
                  )}
                </td>
                {canDelete ? (
                  <td>
                    <DeleteSurvey
                      job={job}
                      busy={deleting === job.last_survey_id}
                      onDelete={onDeleted}
                    />
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Who sent the job's latest survey from the phone, and when — so the board
 * shows whose work each job is without opening it.
 */
function SyncedBy({ job }: { readonly job: Job }): ReactNode {
  const at = job.last_survey_synced_at;
  if (!at) return <span className="muted">No survey yet</span>;
  return (
    <span data-testid="pipeline-synced-by" title={`Arrived ${utcMoment(at)}`}>
      {job.last_survey_synced_by ?? "A former team member"}
      <span className="muted small"> · {longDate(at)}</span>
    </span>
  );
}

/** Deadlines belonging to one job, in the API's own order. */
function deadlinesForJob(
  deadlines: readonly Deadline[],
  jobId: string,
): readonly Deadline[] {
  return deadlines.filter((deadline) => deadline.job_id === jobId);
}

function soonestDays(
  deadlines: readonly { readonly days_remaining: number }[],
): number | null {
  let soonest: number | null = null;
  for (const deadline of deadlines) {
    if (soonest === null || deadline.days_remaining < soonest) {
      soonest = deadline.days_remaining;
    }
  }
  return soonest;
}

function JobRow({
  job,
  deadlineCount,
  worst,
  soonest,
}: {
  readonly job: Job;
  readonly deadlineCount: number;
  readonly worst: string | null;
  readonly soonest: number | null;
}): ReactNode {
  return (
    <tr
      className={
        [
          worst === "breached" ? "is-breached" : null,
          isUnopened(job) ? "is-unread" : null,
        ]
          .filter(Boolean)
          .join(" ") || undefined
      }
    >
      <th scope="row">
        {/* A real href, so the row opens in a new tab like any other link. */}
        <a href={href.job(job.id)}>{job.reference}</a>
        {isUnopened(job) ? <Unopened /> : null}
      </th>
      <td>
        <SyncedBy job={job} />
      </td>
      <td>
        {soonest === null ? (
          <span className="muted">
            {deadlineCount === 0 ? "No clock running" : "—"}
          </span>
        ) : (
          <UrgencyBadge daysRemaining={soonest} />
        )}
      </td>
      <td>
        {job.design_signed_off_at ? (
          isoDay(job.design_signed_off_at)
        ) : (
          <span className="muted">Not signed off</span>
        )}
      </td>
      <td>
        {job.commissioned_on ? (
          isoDay(job.commissioned_on)
        ) : (
          <span className="muted">—</span>
        )}
      </td>
    </tr>
  );
}
